import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink, UIMatch, useMatches } from 'react-router';

import { leftNavigationBarTranslation } from '@/assets/locales/menu/leftNavigationBar.translation.pl.ts';
import { routeDescriptionsTranslation } from '@/assets/locales/menu/routeDescriptions.translation.pl.ts';
import {
  appPageHeaderBreadcrumbsStyles,
  appPageHeaderCurrentStyles,
  appPageHeaderDescriptionStyles,
  appPageHeaderLinkStyles,
  appPageHeaderParentStyles,
  appPageHeaderSeparatorStyles,
  appPageHeaderStyles,
} from '@/components/layout/appPageHeader.styles.ts';
import { BreadcrumbDefinition, RouteHandle } from '@/models/route/Route.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Match = UIMatch<unknown, RouteHandle>;

export const AppPageHeader: React.FC = () => {
  const matches = useMatches() as Match[];
  const { t } = useTranslationWithPrefix('components.breadcrumbs');
  const breadcrumbs = matches.flatMap(match => match.handle?.breadcrumbs ?? []);

  if (breadcrumbs.length === 0) {
    return null;
  }

  const leaf = breadcrumbs[breadcrumbs.length - 1];
  const description =
    leaf.label === 'details' ? undefined : routeDescriptionsTranslation[leaf.label];

  return (
    <Stack sx={appPageHeaderStyles}>
      <Breadcrumbs
        aria-label={t('ariaLabel')}
        separator={<ChevronRightIcon sx={appPageHeaderSeparatorStyles} />}
        sx={appPageHeaderBreadcrumbsStyles}
      >
        {breadcrumbs.map((breadcrumb, index) => {
          const label = getBreadcrumbLabel(breadcrumb, t);
          const isCurrent = index === breadcrumbs.length - 1;

          return isCurrent || !breadcrumb.to ? (
            <Typography
              component={isCurrent ? 'h1' : 'span'}
              key={breadcrumb.to ?? breadcrumb.label}
              sx={isCurrent ? appPageHeaderCurrentStyles : appPageHeaderParentStyles}
              variant={isCurrent ? 'f16Medium' : 'f12Regular'}
            >
              {label}
            </Typography>
          ) : (
            <Link
              component={RouterLink}
              key={breadcrumb.to ?? breadcrumb.label}
              sx={appPageHeaderLinkStyles}
              to={breadcrumb.to}
              variant="f12Regular"
            >
              {label}
            </Link>
          );
        })}
      </Breadcrumbs>
      {description && (
        <Typography sx={appPageHeaderDescriptionStyles} variant="f12Regular">
          {description}
        </Typography>
      )}
    </Stack>
  );
};

const getBreadcrumbLabel = (
  breadcrumb: BreadcrumbDefinition,
  translateDetails: (key: 'details') => string,
): string => {
  if (breadcrumb.label === 'details') {
    return translateDetails('details');
  }

  return (
    (leftNavigationBarTranslation as Record<string, string>)[breadcrumb.label] ?? breadcrumb.label
  );
};
